import CrossIcon from '@atlaskit/icon/core/cross';
import Lozenge from '@atlaskit/lozenge';
import { Box, Pressable, xcss } from '@atlaskit/primitives';
import { messages as databaseMessages } from '@databases/intl/messages';
import React, {
  type FunctionComponent,
  type MouseEventHandler,
  useCallback
} from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from './i18n.ts';
import { getLozengeAppearance } from './select-option-appearance.ts';

const containerStyles = xcss({
  display: 'inline-flex',
  maxWidth: '100%',
  minWidth: '0px'
});

const marginStyles = xcss({
  margin: 'space.050'
});

const disabledStyles = xcss({
  opacity: '0.4'
});

const contentStyles = xcss({
  alignItems: 'center',
  display: 'inline-flex',
  gap: 'space.025',
  maxWidth: '100%',
  minWidth: '0px'
});

const labelStyles = xcss({
  minWidth: '0px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

const removeButtonStyles = xcss({
  alignItems: 'center',
  backgroundColor: 'color.background.neutral.subtle',
  borderRadius: 'radius.small',
  display: 'inline-flex',
  flexShrink: 0,
  height: '16px',
  justifyContent: 'center',
  minWidth: '16px',
  padding: 'space.0',
  width: '16px',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  },
  ':active': {
    backgroundColor: 'color.background.neutral.subtle.pressed'
  }
});

interface SelectOptionTagProps {
  option: { label: string; color: string } | undefined;
  isRemovable?: boolean;
  onRemove?: MouseEventHandler;
  noMargin?: boolean;
  showDisabledValue?: boolean;
}

/**
 * Renders a single option of a Databases select field as an ADS Lozenge.
 */
const SelectOptionTag: FunctionComponent<SelectOptionTagProps> = ({
  option,
  isRemovable = false,
  onRemove,
  noMargin = false,
  showDisabledValue = false
}) => {
  const { formatMessage } = useIntl();
  const label =
    option?.label ?? formatMessage(databaseMessages['board-layout.empty-tag-label']);
  const testId = `database-select-option-tag-${
    option ? option.label.replace(/\s+/g, '-').toLowerCase() : 'undefined'
  }`;
  const handleRemoveMouseDown = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
    },
    []
  );

  const handleRemoveClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      event.stopPropagation();
      onRemove?.(event);
    },
    [onRemove]
  );

  return (
    <Box
      as="span"
      xcss={[
        containerStyles,
        !noMargin && marginStyles,
        showDisabledValue && disabledStyles
      ]}
    >
      <Lozenge
        appearance={getLozengeAppearance(option?.color)}
        maxWidth="180px"
        testId={testId}
      >
        <Box as="span" xcss={contentStyles}>
          <Box as="span" xcss={labelStyles}>
            {label}
          </Box>
          {isRemovable && (
            <Pressable
              aria-label={formatMessage(messages['tag.remove-option-label'], {
                label
              })}
              onClick={handleRemoveClick}
              onMouseDown={handleRemoveMouseDown}
              testId={`close-button-${testId}`}
              xcss={removeButtonStyles}
            >
              <CrossIcon label="" size="small" />
            </Pressable>
          )}
        </Box>
      </Lozenge>
    </Box>
  );
};

export default SelectOptionTag;
